import { useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { PendingInterruptField } from "../hooks/use-workflow-runs"

import { CheckCircle2, Loader2, PauseCircle, XCircle } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { WorkflowRunOutput } from "./workflow-run-output"
import { getApiErrorMessage, getWorkflowRunFromApiError } from "../utils/workflow-run-chat-utils"
import { getRunDenial } from "../utils/workflow-run-denial-utils"
import { resumeWorkflowRun, type WorkflowRunMetadata } from "../utils/workflow-run-resume-utils"

type WorkflowRunResumeCardProps = {
  workflowRun: WorkflowRunMetadata
}

// A file field is recognised by its type prefix (e.g. "file", "file_0"). File
// inputs ride the upload pipeline and can't be re-supplied on resume yet.
function isFileField(field: PendingInterruptField) {
  return field.type.toLowerCase().startsWith("file")
}

type Translate = (key: string, fallback: string) => string

function getStatusLabel(run: WorkflowRunMetadata, denied: boolean, t: Translate) {
  if (denied) return t("workflowLanding.runs.status.denied", "Denied")
  const fallback = run.status.charAt(0).toUpperCase() + run.status.slice(1)
  return t(`workflowLanding.runs.status.${run.status}`, fallback)
}

export function WorkflowRunResumeCard({ workflowRun }: WorkflowRunResumeCardProps) {
  const { t } = useTranslation()
  const [run, setRun] = useState(workflowRun)
  // Legacy single-textarea value (interrupts without `fields`).
  const [inputText, setInputText] = useState("")
  // Per-field values for multi-field user-input pauses, keyed by field.key.
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({})
  const [pendingAction, setPendingAction] = useState<"approve" | "deny" | "input" | null>(null)
  // A run can still be pending without being resumable: the backend downgrades
  // `resumable` when a checkpoint was not durably persisted, while leaving the
  // status at "paused", and this card is mounted on that status alone. So the
  // prompt follows "still pending" and only the controls follow "can actually
  // be resumed" -- gating both on the latter left an amber Paused card with
  // nothing on it but a run id.
  const isPending = run.status === "paused"
  const isPaused = isPending && run.resumable !== false
  // Only a pending run is waiting on the user. Once it reaches a terminal state
  // `pending_interrupt` is null, so computing this unconditionally fell through
  // to the fallback and printed "Human input is required to continue this flow."
  // underneath a Succeeded badge -- while the run's actual answer was shown
  // nowhere at all (#2990).
  const prompt = isPending
    ? run.pending_interrupt?.prompt ||
      t(
        "workflowLanding.runs.inputRequiredDefault",
        "Human input is required to continue this flow."
      )
    : null
  const preview = run.pending_interrupt?.preview
  const isUserInput = run.pending_interrupt?.type === "user_input_required"
  // `fields` only exists on multi-field user-input pauses; WorkflowRunMetadata's
  // pending_interrupt type doesn't surface it, so read it via the FE contract type.
  const fields = (run.pending_interrupt as { fields?: PendingInterruptField[] } | null | undefined)
    ?.fields
  const textFields = (fields ?? []).filter((field) => !isFileField(field))
  const fileFields = (fields ?? []).filter((field) => isFileField(field))
  const hasFieldControls = Boolean(fields && fields.length > 0)
  // The denial marker is split off so a denied run shows its reason, not the
  // raw sentinel (#2998).
  const showOutput = !isPending && run.status !== "failed"
  const denial = getRunDenial(run)
  let StatusIcon = CheckCircle2
  let statusIconClass = "mt-0.5 size-4 shrink-0 text-success"
  // Follows "still pending", not "resumable": a stranded pause would otherwise
  // keep the green success check beside a Paused badge and a waiting prompt --
  // the same finished-and-still-waiting mix this card is meant to stop showing.
  if (isPending) {
    StatusIcon = PauseCircle
    statusIconClass = "mt-0.5 size-4 shrink-0 text-warning-ink "
  } else if (denial.denied || run.status === "failed") {
    StatusIcon = XCircle
    statusIconClass = "mt-0.5 size-4 shrink-0 text-warning-ink "
  }

  const resume = async (
    action: "approve" | "deny" | "input",
    decision: Record<string, unknown>
  ) => {
    setPendingAction(action)
    try {
      const resumedRun = await resumeWorkflowRun(run, decision)
      setRun(resumedRun)
      // Clear collected input so a chained pause doesn't carry stale text.
      setInputText("")
      setFieldValues({})
      toast.success(t("workflowLanding.runs.resumed", "Flow run resumed"))
    } catch (error) {
      const failedRun = getWorkflowRunFromApiError(error)
      if (
        failedRun?.status === "failed" &&
        failedRun.id === run.id &&
        failedRun.workflow_id === run.workflow_id
      ) {
        setRun({
          id: failedRun.id,
          workflow_id: failedRun.workflow_id,
          workflow_title: failedRun.workflow_title ?? run.workflow_title,
          status: failedRun.status,
          trigger: failedRun.trigger,
          output: failedRun.output,
          error: failedRun.error,
          resumable: failedRun.resumable,
          pending_interrupt: failedRun.pending_interrupt,
        })
        toast.error(t("workflowLanding.runs.runFailed", "Flow run failed"), {
          description: failedRun.error ?? undefined,
        })
        return
      }
      // #2964: prefer the backend's own reason ("Run is already being
      // resumed", ...) over axios's status-code message, so the user learns
      // why the card did not move instead of seeing nothing actionable.
      const backendMessage = getApiErrorMessage(error)
      let failureDescription = t(
        "workflowLanding.runs.resumeFailedHint",
        "Open run history and try again."
      )
      if (backendMessage) {
        failureDescription = backendMessage
      } else if (!failedRun && error instanceof Error) {
        failureDescription = error.message
      }
      toast.error(t("workflowLanding.runs.resumeFailed", "Could not resume flow run"), {
        description: failureDescription,
      })
    } finally {
      setPendingAction(null)
    }
  }

  return (
    <section
      data-testid="workflow-run-resume-card"
      className={cn(
        "border-border bg-muted/30 mt-3 rounded-md border p-3 text-sm",
        isPending && "border-warning-ink bg-warning/30"
      )}
    >
      <div className="flex items-start gap-2">
        <StatusIcon className={statusIconClass} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">
              {run.workflow_title || t("workflowLanding.runs.flowRun", "Flow run")}
            </span>
            <span className="bg-background text-muted-foreground rounded px-1.5 py-0.5 text-[11px]">
              {getStatusLabel(run, denial.denied, t)}
            </span>
          </div>
          {prompt && <p className="text-muted-foreground mt-1 whitespace-pre-wrap">{prompt}</p>}
          {preview && <p className="text-muted-foreground mt-1 line-clamp-2">{preview}</p>}
          {/* The finished run's own result -- the thing the user pressed
              Continue to get. Without this the card went terminal showing
              nothing but a status badge and a run id (#2990). A failed run is
              excluded: its `output` is whatever happened to be produced before
              the failure, and presenting that on its own reads as the answer. */}
          {showOutput && <WorkflowRunOutput output={denial.output} reasons={denial.reasons} />}
          {/* Why the run stopped. This was only ever a toast, so once that was
              dismissed the card gave no indication of the failure at all. */}
          {run.status === "failed" && run.error && (
            <p className="text-warning-ink mt-1 whitespace-pre-wrap">{run.error}</p>
          )}
          <p className="text-muted-foreground mt-1 text-[11px]">
            {t("workflowLanding.runs.runId", "Run ID")}: {run.id}
          </p>

          {isPaused && isUserInput && hasFieldControls && (
            <form
              className="mt-2 space-y-3"
              onSubmit={(event) => {
                event.preventDefault()
                // Collect text-field values into field_responses {key: value};
                // file fields can't be supplied on resume yet (see note below).
                const fieldResponses: Record<string, string> = {}
                for (const field of textFields) {
                  fieldResponses[field.key] = (fieldValues[field.key] ?? "").trim()
                }
                void resume("input", { field_responses: fieldResponses })
              }}
            >
              {textFields.map((field) => {
                const label = field.label || field.key
                return (
                  <div key={field.key} className="space-y-1">
                    <label className="text-foreground block text-[13px] font-medium">
                      {label}
                      {field.required && <span className="text-warning-ink"> *</span>}
                    </label>
                    <Input
                      className="bg-background"
                      disabled={pendingAction !== null}
                      required={field.required}
                      value={fieldValues[field.key] ?? ""}
                      onChange={(event) =>
                        setFieldValues((prev) => ({ ...prev, [field.key]: event.target.value }))
                      }
                    />
                  </div>
                )
              })}
              {fileFields.length > 0 && (
                <p className="text-muted-foreground rounded border border-dashed px-2 py-1.5 text-[12px]">
                  {t(
                    "workflowLanding.runs.fileInputsUnavailable",
                    "File inputs can't be provided on resume yet"
                  )}
                  {fileFields.length === 1
                    ? ` (${fileFields[0].label || fileFields[0].key}).`
                    : ` (${fileFields.map((field) => field.label || field.key).join(", ")}).`}
                </p>
              )}
              <Button
                size="sm"
                type="submit"
                disabled={
                  pendingAction !== null ||
                  // A file-only pause has no text controls to fill — don't let
                  // the user silently complete the run blank.
                  textFields.length === 0 ||
                  textFields.some(
                    (field) => field.required && !(fieldValues[field.key] ?? "").trim()
                  )
                }
              >
                {pendingAction === "input" && <Loader2 className="mr-1 size-3 animate-spin" />}
                {t("workflowLanding.runs.continue", "Continue")}
              </Button>
            </form>
          )}

          {isPaused && isUserInput && !hasFieldControls && (
            <form
              className="mt-2 space-y-2"
              onSubmit={(event) => {
                event.preventDefault()
                const text = inputText.trim()
                if (text) {
                  void resume("input", { text })
                }
              }}
            >
              <Textarea
                required
                className="bg-background min-h-20"
                disabled={pendingAction !== null}
                value={inputText}
                onChange={(event) => setInputText(event.target.value)}
              />
              <Button
                disabled={pendingAction !== null || !inputText.trim()}
                size="sm"
                type="submit"
              >
                {pendingAction === "input" && <Loader2 className="mr-1 size-3 animate-spin" />}
                {t("workflowLanding.runs.continue", "Continue")}
              </Button>
            </form>
          )}

          {isPaused && !isUserInput && (
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                disabled={pendingAction !== null}
                size="sm"
                onClick={() => void resume("approve", { decision: "approve" })}
              >
                {pendingAction === "approve" && <Loader2 className="mr-1 size-3 animate-spin" />}
                {t("workflowLanding.runs.approve", "Approve")}
              </Button>
              <Button
                disabled={pendingAction !== null}
                size="sm"
                variant="outline"
                onClick={() => void resume("deny", { decision: "deny" })}
              >
                {pendingAction === "deny" && <Loader2 className="mr-1 size-3 animate-spin" />}
                {t("workflowLanding.runs.deny", "Deny")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
